/* HabitatsCard — Profile widget showing per-habitat exploration progress and
 * the tier badges the player has earned. Reads GET /api/habitats/me.
 * Renders quietly if the player has no observations yet. */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Compass } from 'lucide-react';
import api from '../lib/api';

const TIER_COLOR = { explorer: '#84cc16', expert: '#f59e0b', master: '#a855f7' };

export default function HabitatsCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/habitats/me')
      .then(r => setData(r.data))
      .catch(() => setData({ habitats: [] }));
  }, []);

  if (!data) return null;
  if (!data.habitats || data.habitats.length === 0) return null;

  return (
    <div className="rounded-3xl glass border border-white/10 p-4 space-y-3" data-testid="habitats-card">
      <div className="flex items-center gap-2">
        <Compass className="w-3.5 h-3.5 text-accent" />
        <p className="text-[10px] uppercase tracking-widest text-accent">Expedition progress</p>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {data.habitats.map(h => {
          const tierColor = TIER_COLOR[h.tier] || '#94a3b8';
          const nextText = h.next
            ? `${h.next.needed} more → ${h.next.label}`
            : 'Master reached';
          return (
            <motion.div
              key={h.habitat}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`habitat-${h.habitat}`}
              className="rounded-2xl border border-white/10 bg-black/40 p-3 space-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium truncate" style={{ color: h.color }}>{h.label}</span>
                {h.tier_label && (
                  <span
                    className="text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5"
                    style={{ background: `${tierColor}22`, color: tierColor, border: `1px solid ${tierColor}55` }}
                  >{h.tier_label}</span>
                )}
              </div>
              <p className="font-mono text-lg tabular-nums" style={{ color: h.color }}>{h.unique_species}</p>
              <p className="text-[9px] uppercase tracking-widest text-white/50">{nextText}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
